<template>
  <b-form class="group-notification-form" @submit.prevent="createNotificationGroup">
    <ConveyRequiredStatement class="mb-2" />
    <b-form-group label="Campaign:" class="required-form-field" style="font-weight:500">
      <b-dropdown
        ref="campaignPickerDropdown"
        class="campaign-picker-dropdown"
        split
        :variant="isDarkMode ? 'dark' : 'light'"
      >
        <template #button-content>
          <span
            class="d-inline-flex w-100 h-100 align-items-center"
            style="padding: .375rem .75rem;"
            @click="showCampaignPickerDropdown"
          >
            {{ selectedCampaign ? selectedCampaign.label : campaignPickerLabel }}
          </span>
        </template>
        <b-dropdown-item
          v-for="option in campaigns"
          :key="option.value"
          @click="selectCampaign(option)"
        >
          {{ option.label }}
        </b-dropdown-item>
      </b-dropdown>
      <div v-if="!campaignValidtaion" class="invalid-feedback d-block">Campaign field is required!</div>
    </b-form-group>
    <b-form-group v-if="selectedCampaign && selectedCampaign.groups && selectedCampaign.groups.length > 0">
      <MultiSelectDropdown
        v-model="selectedGroups"
        :options="groupOptions"
        placeholder="Select Groups"
        selectedAllLabel="All Groups"
        showSelectAll
        showDeselectAll
        showSearch
        selectAllAsDefault
        optimized
      />
      <div v-if="selectedGroups.length === 0" class="valid-feedback d-block">
        {{`If no group is selected, it will be sent to ${totalSubscribers} subscribers.`}}
      </div>
    </b-form-group>
    <b-form-group label="Event Title:" class="required-form-field" style="font-weight:500">
      <b-form-input
        v-model="eventName"
        placeholder="Event Title (Internal Only)"
        @input="eventNameTouched = true"
      />
      <div v-if="!eventNameValidation" class="invalid-feedback d-block">Event title field is required!</div>
    </b-form-group>
    <b-form-group :state="eventMessageValidation" label="Event Message:" class="required-form-field" style="font-weight:500">
      <b-form-textarea
        v-model="eventMessage"
        style="resize:none;"
        rows="4"
        placeholder="Enter a message..."
        @input="eventMessageTouched = true"
      >
      </b-form-textarea>
      <div v-if="!eventMessageValidation" class="invalid-feedback d-block">Event message field is required!</div>
    </b-form-group>
    <div class="group-notification-form--actions bg-light d-flex align-items-center justify-content-between">
      <div class="d-flex flex-column">
        <span>{{ `Total Characters: ${eventMessageCharacters}` }}</span>
        <span>{{ `SMS Segments: ${smsSegments}` }}</span>
      </div>
      <b-button
        type="submit"
        pill
        variant="info"
        :disabled="isCreatingCampaignNotification"
      >
        <template v-if="!isCreatingCampaignNotification">{{ createCTALabel }}</template>
        <i v-else class="fa fa-spinner fa-spin"></i>
      </b-button>
    </div>
  </b-form>
</template>

<script>
/* eslint-disable */
import { EventBus } from "@/events/eventBus"
import { mapGetters } from "vuex";
import MultiSelectDropdown from "@/components/common/MultiSelectDropdown.vue";
import formatter from "@/mixins/formatter";
import ConveyRequiredStatement from "@/components/ConveyRequiredStatement";

export default {
  name: "GroupNotificationForm",
  mixins: [formatter],
  props: {},
  components: { MultiSelectDropdown, ConveyRequiredStatement },
  data() {
    return {
      eventMessage: null,
      eventName: null,
      groups: [],
      campaigns: [],
      selectedGroups: [],
      groupOptions: [],
      selectedCampaign: null,
      isLoadingCampaings: false,
      isCreatingCampaignNotification: false,
      campaignTouched: false,
      eventNameTouched: false,
      eventMessageTouched: false,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "currentUser", "isDarkMode"]),
    ...mapGetters("agency", ["currentAgency", "agencySettings"]),
    ...mapGetters("conversations", ["currentConversation"]),
    ...mapGetters("campaigns", ["currentCampaignEvents", "lastSelectedCampaignId"]),
    campaignPickerLabel() {
      if (this.isLoadingCampaings)
        return "Loading...";
      else
        return this.campaigns.length === 0 ? "You have no campaigns" : "Select campaign";
    },
    campaignValidtaion() {
      if (this.campaignTouched) {
        return this.selectedCampaign && this.selectedCampaign.label;
      }
      return true;
    },
    eventNameValidation() {
      if (this.eventNameTouched) {
        return this.eventName && this.eventName.length > 0;
      }
      return true;
    },
    eventMessageValidation() {
      if (this.eventMessageTouched) {
        return this.eventMessage && this.eventMessage.length > 0;
      }
      return true;
    },
    eventMessageCharacters() {
      return this.eventMessage ? this.eventMessage.length : 0;
    },
    smsSegments() {
      return Math.ceil(this.eventMessageCharacters / 160);
    },
    formValidation() {
      return this.campaignValidtaion && this.eventMessageValidation && this.eventNameValidation;
    },
    totalSubscribers() {
      if (this.groupOptions.length > 0) {
        let totalSubscribers = 0;
        if (this.selectedGroups && this.selectedGroups.length > 0) {
          this.selectedGroups.forEach(groupId => {
            const groupOption = this.groupOptions.find(option => option.value === groupId);
            if (groupOption) {
              totalSubscribers += groupOption.totalMembers;
            }
          });
        } else {
          totalSubscribers = this.groupOptions.reduce((accum, item) => accum + Number(item.totalMembers), 0);
        }
        return this.formatNumber(totalSubscribers);
      }
      return 0;
    },
    createCTALabel() {
      if (this.groupOptions.length > 0) {
        if (this.selectedGroups && this.selectedGroups.length > 0) {
          return `Send to ${this.totalSubscribers} Subscribers`;
        } else {
          return "Create Campaign Event";
        }
      }
      return "Create Campaign Event";
    },
  },
  created() {
    this.getCampaigns();
  },
  methods: {
    showCampaignPickerDropdown() {
      this.$refs.campaignPickerDropdown.show();
    },
    selectCampaign(campaign) {
      this.campaignTouched = true;
      this.selectedCampaign = {...campaign};
      const { groups, value: selectedCampaignId } = campaign;
      if (Array.isArray(groups) && groups.length > 0) {
        this.groupOptions = groups.map(group => {
          const { id, group_name, total_members } = group;
          const formattedTotalMembers = this.formatNumber(Number(total_members));
          return {
            label: `${group_name} (${formattedTotalMembers})`,
            value: id,
            totalMembers: total_members,
          };
        });
        this.selectedGroups = this.groupOptions.map(item => item.value);
      } else {
        this.groupOptions = [];
        this.selectedGroups = [];
      }
      this.$store.commit("campaigns/setLastSelectedCampaignId", selectedCampaignId);
    },
    async getCampaigns() {
      try {
        this.isLoadingCampaings = true;
        const resp = await this.$ConveyApi.campaignsApi.getCampaigns({params: {}});
        this.campaigns = resp.data.data.map(t => ({
          label: t.campaign_name,
          value: t.campaign_id,
          groups: t.groups
        }));
        if (this.lastSelectedCampaignId) {
          const findCampaign = this.campaigns.find(option => option.value === this.lastSelectedCampaignId);
          if (findCampaign) {
            this.selectCampaign(findCampaign);
          } else if (this.campaigns.length > 0) {
            this.selectCampaign(this.campaigns[0]);
          }
        } else if (this.campaigns.length > 0) {
          this.selectCampaign(this.campaigns[0]);
        }
      } catch (err) {
        console.log("[GroupNotificationForm:getCampaigns] ERROR: ", err);
        this.campaigns = [];
      } finally {
        this.isLoadingCampaings = false;
        this.eventMessage = "";
        this.eventName = "";
      }
    },
    async createNotificationGroup() {
      this.campaignTouched = true;
      this.eventNameTouched = true;
      this.eventMessageTouched = true;
      if (this.formValidation && confirm("Are you sure you want to create a new campaign event?")) {
        try {
          this.isCreatingCampaignNotification = true;
          const payload = {
            "event_name": this.eventName,
            "event_message": this.eventMessage,
          };

          if (this.selectedGroups && this.selectedGroups.length > 0) {
            payload["groups"] = [...this.selectedGroups];
          } else if(this.groupOptions.length > 0) {
            payload["groups"] = this.groupOptions.map(item => item.value);
          }

          const resp = await this.$ConveyApi.campaignsApi.createCampaignEvent({ campaignId: this.selectedCampaign.value, payload });
          if (resp.data.data && resp.data.data.length > 0) {
            const campaignEvent = resp.data.data[0];
            const { event_id } = campaignEvent;
            this.$router.push({path: `/campaigns/${this.selectedCampaign.value}/events/${event_id}`}).catch(()=>{});
            EventBus.$emit("close-modal");
          } else {
            this.$toastr("error", "Error Creating Group Notification");
            EventBus.$emit("close-modal");
          }
        } catch(err) {
          console.log("[GroupNotificationForm:createNotificationGroup] ERROR: ", err);
          this.$toastr("error", "Error Creating Group Notification (" + err.response.status + ")");
        } finally {
          this.isCreatingCampaignNotification = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.group-notification-form {
  .campaign-picker-dropdown {
    width: 100%;
    min-width: 120px;
    &.btn-group {
      >button {
        text-align: left;
        &:first-child {
          padding: 0;
        }
      }
      .dropdown-toggle-split {
        flex: unset;
      }
    }
  }
  &--actions {
    margin-left: -16px;
    margin-right: -16px;
    margin-bottom: -16px;
    padding: 16px;
    border-radius: 0 0 1rem 1rem;
  }
}
</style>
